import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type CuentaBancaria = {
  id: number; nombre: string; tipo: string; banco: string | null; numeroCuenta: string | null;
  saldoInicial: number; saldoActual: number; activa: boolean;
};
type Movimiento = {
  id: number; tipo: "ingreso" | "egreso" | "transferencia"; cuentaBancariaId: number; cuentaNombre: string;
  cuentaBancariaDestinoId: number | null; cuentaDestinoNombre: string | null; concepto: string;
  descripcion: string | null; valor: number; fecha: string; comprobanteId: number | null;
};
type Cxp = {
  id: number; recepcionId: number; proveedorId: number; proveedorNombre: string; loteCodigo: string;
  saldo: number; fechaVencimiento: string; estado: string;
};
type Extracto = {
  id: number; cuentaBancariaId: number; cuentaNombre: string; periodoDesde: string; periodoHasta: string;
  totalLineas: number; lineasConciliadas: number; cargadoEn: string;
};
type FlujoCaja = { saldoActualBancos: number; proyeccionMensual: { mes: string; entradasProyectadas: number; salidasProyectadas: number }[] };

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const TIPO_CUENTA_LABEL: Record<string, string> = { ahorros: "Ahorros", corriente: "Corriente", caja_menor: "Caja menor", tarjeta: "Tarjeta" };
const TIPO_MOV_BADGE: Record<string, string> = {
  ingreso: "bg-accent-light text-accent-dark", egreso: "bg-red-50 text-red-700", transferencia: "bg-amber-50 text-amber-700",
};

export function Tesoreria() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("tesoreria:crear");
  const puedeConciliar = tienePermiso("tesoreria:conciliar");

  const [tab, setTab] = useState("cuentas");
  const [cuentas, setCuentas] = useState<CuentaBancaria[]>([]);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [cxp, setCxp] = useState<Cxp[]>([]);
  const [extractos, setExtractos] = useState<Extracto[]>([]);
  const [flujo, setFlujo] = useState<FlujoCaja | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [c, m, p, e, f] = await Promise.all([
        api.get("/tesoreria/cuentas"),
        api.get("/tesoreria/movimientos"),
        api.get("/tesoreria/cartera-cxp"),
        api.get("/tesoreria/extractos"),
        api.get("/tesoreria/flujo-caja"),
      ]);
      setCuentas(c); setMovimientos(m); setCxp(p); setExtractos(e); setFlujo(f);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar tesorería.");
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  return (
    <Shell titulo="Tesorería" breadcrumb="Tesorería">
      <p className="text-sm text-textmuted mb-4">
        Cuentas bancarias (sub-libro de la cuenta contable única "1110 Bancos", igual a como clientes y proveedores
        son auxiliares de sus cuentas en Contabilidad), movimientos manuales de caja/bancos, cuentas por pagar a
        proveedores generadas automáticamente al recibir materia prima, conciliación bancaria y una proyección
        simple de caja a partir de la cartera pendiente por cobrar y por pagar.
      </p>
      <Tabs
        tabs={[
          { key: "cuentas", label: "Cuentas bancarias" },
          { key: "movimientos", label: "Movimientos" },
          { key: "cxp", label: "Cuentas por pagar" },
          { key: "conciliacion", label: "Conciliación bancaria" },
          { key: "flujo", label: "Flujo de caja proyectado" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "cuentas" && <TabCuentas cuentas={cuentas} puedeCrear={puedeCrear} recargar={cargar} />}
          {tab === "movimientos" && <TabMovimientos movimientos={movimientos} cuentas={cuentas} puedeCrear={puedeCrear} recargar={cargar} />}
          {tab === "cxp" && <TabCxp cxp={cxp} cuentas={cuentas} puedeCrear={puedeCrear} recargar={cargar} />}
          {tab === "conciliacion" && <TabConciliacion extractos={extractos} cuentas={cuentas} puedeConciliar={puedeConciliar} recargar={cargar} />}
          {tab === "flujo" && <TabFlujo flujo={flujo} />}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- cuentas bancarias
function TabCuentas({ cuentas, puedeCrear, recargar }: { cuentas: CuentaBancaria[]; puedeCrear: boolean; recargar: () => void }) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("ahorros");
  const [banco, setBanco] = useState("");
  const [numeroCuenta, setNumeroCuenta] = useState("");
  const [saldoInicial, setSaldoInicial] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/tesoreria/cuentas", { nombre, tipo, banco: banco || null, numeroCuenta: numeroCuenta || null, saldoInicial });
      setModalAbierto(false);
      setNombre(""); setBanco(""); setNumeroCuenta(""); setSaldoInicial(0);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear la cuenta bancaria.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Cuentas bancarias</h3>
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva cuenta</button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cuentas.map((c) => (
          <div key={c.id} className="card p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-semibold text-slate-800">{c.nombre}</div>
              <span className="badge bg-slate-100 text-slate-500 text-[10px]">{TIPO_CUENTA_LABEL[c.tipo] || c.tipo}</span>
            </div>
            <div className="text-xs text-textmuted">{c.banco || "—"}{c.numeroCuenta ? ` · ${c.numeroCuenta}` : ""}</div>
            <div className="text-lg font-bold text-slate-800 mt-2">{money(c.saldoActual)}</div>
            <div className="text-[11px] text-textmuted">Saldo inicial: {money(c.saldoInicial)}</div>
            {!c.activa && <span className="badge bg-red-50 text-red-700 text-[10px] mt-2 inline-block">Inactiva</span>}
          </div>
        ))}
        {cuentas.length === 0 && <p className="text-sm text-textmuted col-span-3">Sin cuentas bancarias registradas.</p>}
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nueva cuenta bancaria</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
                <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="ahorros">Ahorros</option>
                  <option value="corriente">Corriente</option>
                  <option value="caja_menor">Caja menor</option>
                  <option value="tarjeta">Tarjeta</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Saldo inicial</label>
                <input className="input-field" type="number" min={0} step="1000" value={saldoInicial} onChange={(e) => setSaldoInicial(Number(e.target.value))} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Banco (opcional)</label>
              <input className="input-field" value={banco} onChange={(e) => setBanco(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Número de cuenta (opcional)</label>
              <input className="input-field" value={numeroCuenta} onChange={(e) => setNumeroCuenta(e.target.value)} />
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear cuenta"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- movimientos
function TabMovimientos({ movimientos, cuentas, puedeCrear, recargar }: {
  movimientos: Movimiento[]; cuentas: CuentaBancaria[]; puedeCrear: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [tipo, setTipo] = useState<"ingreso" | "egreso" | "transferencia">("ingreso");
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [cuentaBancariaDestinoId, setCuentaBancariaDestinoId] = useState<number | "">("");
  const [concepto, setConcepto] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cuentaBancariaId || valor <= 0 || !concepto) {
      setError("Cuenta bancaria, concepto y un valor mayor a cero son obligatorios.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/tesoreria/movimientos", {
        tipo, cuentaBancariaId: Number(cuentaBancariaId),
        cuentaBancariaDestinoId: tipo === "transferencia" && cuentaBancariaDestinoId ? Number(cuentaBancariaDestinoId) : null,
        concepto, descripcion: descripcion || null, valor,
      });
      setModalAbierto(false);
      setConcepto(""); setDescripcion(""); setValor(0);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el movimiento.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Movimientos de tesorería</h3>
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo movimiento</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Tipo</th><th className="table-th">Cuenta</th><th className="table-th">Destino</th>
            <th className="table-th">Concepto</th><th className="table-th">Valor</th><th className="table-th">Fecha</th>
          </tr></thead>
          <tbody>
            {movimientos.map((m) => (
              <tr key={m.id} className="border-b border-borderc last:border-0">
                <td className="table-td"><span className={`badge ${TIPO_MOV_BADGE[m.tipo]}`}>{m.tipo}</span></td>
                <td className="table-td">{m.cuentaNombre}</td>
                <td className="table-td text-xs">{m.cuentaDestinoNombre || "—"}</td>
                <td className="table-td text-xs">{m.concepto}</td>
                <td className="table-td font-semibold">{money(m.valor)}</td>
                <td className="table-td text-xs">{m.fecha}</td>
              </tr>
            ))}
            {movimientos.length === 0 && (
              <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin movimientos registrados todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-lg p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo movimiento de tesorería</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
              <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
                <option value="ingreso">Ingreso</option>
                <option value="egreso">Egreso</option>
                <option value="transferencia">Transferencia entre cuentas propias</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {tipo === "transferencia" ? "Cuenta origen" : "Cuenta bancaria"}
                </label>
                <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
                  <option value="">Seleccionar...</option>
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              {tipo === "transferencia" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta destino</label>
                  <select className="input-field" value={cuentaBancariaDestinoId} onChange={(e) => setCuentaBancariaDestinoId(e.target.value ? Number(e.target.value) : "")} required>
                    <option value="">Seleccionar...</option>
                    {cuentas.filter((c) => c.id !== cuentaBancariaId).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Concepto</label>
              <input className="input-field" value={concepto} onChange={(e) => setConcepto(e.target.value)}
                     placeholder={tipo === "ingreso" ? "Ej: Rendimientos financieros" : tipo === "egreso" ? "Ej: Gastos bancarios" : "Ej: Fondeo de caja menor"} required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción (opcional)</label>
              <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
              <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar movimiento"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const AGING_BUCKETS_CXP: { key: string; label: string }[] = [
  { key: "porVencer", label: "Por vencer" },
  { key: "dias_0_30", label: "0-30 días" },
  { key: "dias_31_60", label: "31-60 días" },
  { key: "dias_61_90", label: "61-90 días" },
  { key: "masDe90", label: "Más de 90 días" },
];

// ---------------------------------------------------------------- cuentas por pagar
function TabCxp({ cxp, cuentas, puedeCrear, recargar }: { cxp: Cxp[]; cuentas: CuentaBancaria[]; puedeCrear: boolean; recargar: () => void }) {
  const [pagando, setPagando] = useState<Cxp | null>(null);
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [aging, setAging] = useState<any | null>(null);

  useEffect(() => { api.get("/tesoreria/cartera-cxp-aging").then(setAging).catch(() => setAging(null)); }, [cxp]);

  const pagar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pagando || !cuentaBancariaId) return;
    setEnviando(true);
    setError(null);
    try {
      await api.put(`/tesoreria/cartera-cxp/${pagando.id}/pagar`, { cuentaBancariaId: Number(cuentaBancariaId) });
      setPagando(null);
      setCuentaBancariaId("");
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el pago.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Cuentas por pagar a proveedores</h3>
      {aging && (
        <div className="card p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Antigüedad de saldos · corte {aging.corte}</p>
          <div className="grid grid-cols-5 gap-3">
            {AGING_BUCKETS_CXP.map((b) => (
              <div key={b.key} className={`rounded-md px-3 py-2 ${b.key === "masDe90" && aging.resumen[b.key] > 0 ? "bg-red-50" : "bg-surface"}`}>
                <div className="text-[10px] text-textmuted uppercase">{b.label}</div>
                <div className={`text-sm font-semibold ${b.key === "masDe90" && aging.resumen[b.key] > 0 ? "text-red-700" : "text-slate-800"}`}>${aging.resumen[b.key].toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm font-semibold text-navy border-t border-borderc pt-2 mt-3">
            <span>Total por pagar</span><span>${aging.total.toLocaleString()}</span>
          </div>
        </div>
      )}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Proveedor</th><th className="table-th">Recepción</th><th className="table-th">Saldo</th>
            <th className="table-th">Vence</th><th className="table-th">Estado</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {cxp.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td">{c.proveedorNombre}</td>
                <td className="table-td font-mono text-xs">{c.loteCodigo}</td>
                <td className="table-td font-semibold">{money(c.saldo)}</td>
                <td className="table-td text-xs">{c.fechaVencimiento}</td>
                <td className="table-td"><span className={`badge ${c.estado === "pagada" ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>{c.estado}</span></td>
                <td className="table-td text-right">
                  {puedeCrear && c.estado !== "pagada" && (
                    <button className="text-navy hover:underline text-xs font-semibold" onClick={() => setPagando(c)}>Pagar</button>
                  )}
                </td>
              </tr>
            ))}
            {cxp.length === 0 && (
              <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin cuentas por pagar registradas.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pagando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={pagar} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Pagar a {pagando.proveedorNombre}</h3>
            <p className="text-sm text-textmuted">Valor a pagar: <span className="font-semibold text-slate-800">{money(pagando.saldo)}</span></p>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta bancaria de origen</label>
              <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
                <option value="">Seleccionar...</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({money(c.saldoActual)})</option>)}
              </select>
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setPagando(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Pagando..." : "Confirmar pago"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- conciliación bancaria
function TabConciliacion({ extractos, cuentas, puedeConciliar, recargar }: {
  extractos: Extracto[]; cuentas: CuentaBancaria[]; puedeConciliar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [periodoDesde, setPeriodoDesde] = useState("");
  const [periodoHasta, setPeriodoHasta] = useState("");
  const [lineas, setLineas] = useState([{ fecha: "", valor: 0, descripcion: "" }]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const agregarLinea = () => setLineas([...lineas, { fecha: "", valor: 0, descripcion: "" }]);
  const actualizarLinea = (i: number, campo: string, valor: any) => {
    const copia = [...lineas];
    (copia[i] as any)[campo] = valor;
    setLineas(copia);
  };

  const cargarExtracto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cuentaBancariaId || !periodoDesde || !periodoHasta) {
      setError("Cuenta bancaria, periodoDesde y periodoHasta son obligatorios.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/tesoreria/extractos", {
        cuentaBancariaId: Number(cuentaBancariaId), periodoDesde, periodoHasta,
        lineas: lineas.filter((l) => l.fecha && l.valor !== 0),
      });
      setModalAbierto(false);
      setLineas([{ fecha: "", valor: 0, descripcion: "" }]);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar el extracto.");
    } finally {
      setEnviando(false);
    }
  };

  const conciliar = async (extractoId: number) => {
    setMensaje(null);
    try {
      const r = await api.post(`/tesoreria/extractos/${extractoId}/conciliar`);
      setMensaje(`${r.conciliadasEnEsteProceso} línea(s) conciliada(s) en este proceso. Pendientes: ${r.pendientes} de ${r.totalLineas}.`);
      recargar();
    } catch (err) {
      setMensaje(err instanceof ApiError ? err.message : "No se pudo conciliar el extracto.");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Extractos bancarios</h3>
        {puedeConciliar && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Cargar extracto</button>}
      </div>
      {mensaje && <div className="rounded-md bg-slate-50 border border-borderc px-3 py-2 text-sm text-slate-700 mb-3">{mensaje}</div>}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Cuenta</th><th className="table-th">Periodo</th><th className="table-th">Líneas conciliadas</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {extractos.map((e) => (
              <tr key={e.id} className="border-b border-borderc last:border-0">
                <td className="table-td">{e.cuentaNombre}</td>
                <td className="table-td text-xs">{e.periodoDesde} — {e.periodoHasta}</td>
                <td className="table-td">{e.lineasConciliadas} / {e.totalLineas}</td>
                <td className="table-td text-right">
                  {puedeConciliar && e.lineasConciliadas < e.totalLineas && (
                    <button className="text-navy hover:underline text-xs font-semibold" onClick={() => conciliar(e.id)}>Conciliar</button>
                  )}
                </td>
              </tr>
            ))}
            {extractos.length === 0 && (
              <tr><td colSpan={4} className="table-td text-center text-textmuted py-6">Sin extractos cargados todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={cargarExtracto} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Cargar extracto bancario</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta bancaria</label>
                <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
                  <option value="">Seleccionar...</option>
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Periodo desde</label>
                <input className="input-field" type="date" value={periodoDesde} onChange={(e) => setPeriodoDesde(e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Periodo hasta</label>
                <input className="input-field" type="date" value={periodoHasta} onChange={(e) => setPeriodoHasta(e.target.value)} required />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">
                Líneas del extracto (valor positivo = entrada de dinero; negativo = salida)
              </label>
              <div className="space-y-2">
                {lineas.map((l, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2">
                    <input className="input-field" type="date" value={l.fecha} onChange={(e) => actualizarLinea(i, "fecha", e.target.value)} />
                    <input className="input-field" type="number" step="1000" placeholder="Valor" value={l.valor} onChange={(e) => actualizarLinea(i, "valor", Number(e.target.value))} />
                    <input className="input-field" placeholder="Descripción" value={l.descripcion} onChange={(e) => actualizarLinea(i, "descripcion", e.target.value)} />
                  </div>
                ))}
              </div>
              <button type="button" className="btn-ghost text-xs mt-2" onClick={agregarLinea}>+ Agregar línea</button>
            </div>

            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Cargando..." : "Cargar extracto"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- flujo de caja proyectado
function TabFlujo({ flujo }: { flujo: FlujoCaja | null }) {
  if (!flujo) return null;
  return (
    <div>
      <div className="card p-5 mb-4">
        <div className="text-xs text-textmuted">Saldo actual en bancos (cuentas activas)</div>
        <div className="text-2xl font-bold text-slate-800">{money(flujo.saldoActualBancos)}</div>
      </div>
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Proyección por mes de vencimiento</h3>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Mes</th><th className="table-th">Entradas proyectadas (CxC pendiente)</th>
            <th className="table-th">Salidas proyectadas (CxP pendiente)</th><th className="table-th">Neto</th>
          </tr></thead>
          <tbody>
            {flujo.proyeccionMensual.map((p) => (
              <tr key={p.mes} className="border-b border-borderc last:border-0">
                <td className="table-td font-semibold">{p.mes}</td>
                <td className="table-td text-accent-dark">{money(p.entradasProyectadas)}</td>
                <td className="table-td text-red-700">{money(p.salidasProyectadas)}</td>
                <td className="table-td font-semibold">{money(p.entradasProyectadas - p.salidasProyectadas)}</td>
              </tr>
            ))}
            {flujo.proyeccionMensual.length === 0 && (
              <tr><td colSpan={4} className="table-td text-center text-textmuted py-6">Sin cartera pendiente por cobrar o pagar.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-textmuted mt-3">
        Proyección basada en compromisos ya registrados en el sistema (cartera por cobrar de Comercial y cuentas por
        pagar a proveedores de Tesorería), no en un presupuesto — para flujo de caja presupuestado ver el módulo
        Gerencial.
      </p>
    </div>
  );
}
